import { all, call, put, takeLatest, select } from 'redux-saga/effects';

import { TournamentOpen } from 'actions/actionTypes';
import * as actions from 'actions/tournament/open';

import {
  callShopTournamentRawData,
  callShopTournamentTerms,
  callShopTournamentCourseList,
  callShopTournamentCourseDetails,
  callAddTournament,
  callModifyTournament,
} from 'network/apis/tournament/open';

// 매장 대회 로우 데이터 조회
function* loadShopTournamentRawDataRequestSaga() {
  try {
    const tournamentOpen = yield select((state) => state.tournamentOpen);

    const response = yield call(callShopTournamentRawData, tournamentOpen.tournamentId);

    yield put(actions.loadShopTournamentRawDataRequestSuccess(response.data));
  } catch (error) {
    yield put(actions.loadShopTournamentRawDataRequestFailure(error.message));
  }
}

// 매장 대회 약관 조회
function* loadShopTournamentTermsRequestSaga() {
  try {
    const language = yield select((state) => state.language);
    let lang;

    switch(language.currentLanguage) {
      case 'ko-kr':
        lang = 'Terms_KR';
        break;
      case 'en-us':
        lang = 'Terms_US';
        break;
      case 'zh-cn':
        lang = 'Terms_CN';
        break;
      case 'ja-jp':
        lang = 'Terms_JP';
        break;
      case 'zh-tw':
        lang = 'Terms_TW';
        break;
      default:
        lang = 'Terms_KR';
        break;
    }

    const response = yield call(callShopTournamentTerms, lang);

    yield put(actions.loadShopTournamentTermsRequestSuccess(response.data.settingValue));
  } catch (error) {
    yield put(actions.loadShopTournamentTermsRequestFailure(error.message));
  }
}

// 매장 대회 코스 목록 조회
function* loadShopTournamentCourseListRequestSaga() {
  try {
    const response = yield call(callShopTournamentCourseList);

    yield put(actions.loadShopTournamentCourseListRequestSuccess(response.data));
  } catch (error) {
    yield put(actions.loadShopTournamentCourseListRequestFailure(error.message));
  }
}

// 매장 대회 코스 상세 조회
function* fetchShopTournamentCourseDetails(courseId) {
  try {
    const response = yield call(callShopTournamentCourseDetails, courseId);
    yield put(actions.loadShopTournamentCourseDetailsRequestSuccess(response.data));
  } catch (error) {
    yield put(actions.loadShopTournamentCourseDetailsRequestFailure(error.message));
  }
}

function* loadShopTournamentCourseDetailsRequestSaga() {
    const tournamentOpen = yield select((state) => state.tournamentOpen);
    yield all(tournamentOpen.data.courseRounds.map((courseRound) =>
        call(fetchShopTournamentCourseDetails, courseRound.courseId)
    ));
}

// 매장 대회 개설
function* addShopTournamentRequestSaga() {
  try {
    const tournamentOpen = yield select((state) => state.tournamentOpen);

    const awards = [...tournamentOpen.data.awards];

    let newAwards = awards.map((awards) => {
      if (awards.awardItems === 'HoleInOne' || awards.awardItems === 'CourseRoundPlayCount') {
        // 홀인원, 참가 라운드 횟수는 종합 랭킹이므로 코스ID 지정하지 않도록 예외처리
        return { ...awards, courseId: '' };
      } else {
        if (tournamentOpen.data.courseRounds.length === 1) {
          return { ...awards, courseId: tournamentOpen.data.courseRounds[0].courseId };
        } else {
          return { ...awards, courseId: null };
        }
      }
    });

    const data = {
      tournamentTypeCode: tournamentOpen.data.tournamentTypeCode,
      title: tournamentOpen.data.title,
      info: tournamentOpen.data.info,
      summary: tournamentOpen.data.summary,
      thumbnailKey: tournamentOpen.data.thumbnailKey,
      isParticipationTargetLimit: tournamentOpen.data.isParticipationTargetLimit,
      participationTargets: tournamentOpen.data.participationTargets,
      participationGrades: tournamentOpen.data.participationGrades,
      isParticipationConditionsLimit: tournamentOpen.data.isParticipationConditionsLimit,
      participationConditions: tournamentOpen.data.participationConditions,
      startTime: tournamentOpen.data.startTime,
      endTime: tournamentOpen.data.endTime,
      password: tournamentOpen.data.password,
      isShotLiveBroadcast: tournamentOpen.data.isShotLiveBroadcast,
      courseRounds: tournamentOpen.data.courseRounds,
      gameHoleType: tournamentOpen.data.gameHoleType,
      gameSettingLevel: tournamentOpen.data.gameSettingLevel,
      difficulty: tournamentOpen.data.difficulty,
      greenSpeed: tournamentOpen.data.greenSpeed,
      greenHardness: tournamentOpen.data.greenHardness,
      swingPlate: tournamentOpen.data.swingPlate,
      holecupLocation: tournamentOpen.data.holecupLocation,
      concede: tournamentOpen.data.concede,
      greenLocation: tournamentOpen.data.greenLocation,
      mulliganCount: tournamentOpen.data.mulliganCount,
      puttingLineCount: tournamentOpen.data.puttingLineCount,
      windSpeed: tournamentOpen.data.windSpeed,
      isCalculationGameRoundCount: tournamentOpen.data.isCalculationGameRoundCount,
      isCalculationBackCount: tournamentOpen.data.isCalculationBackCount,
      isCalculationLastRoundStartTime: tournamentOpen.data.isCalculationLastRoundStartTime,
      isAward: tournamentOpen.data.isAward,
      awards: newAwards,
      participationShops: tournamentOpen.data.participationShops,
      isParticipationGradeLimit: tournamentOpen.data.isParticipationGradeLimit,
      isUseGuideDistance: tournamentOpen.data.isUseGuideDistance,
    };

    const response = yield call(callAddTournament, data);

    yield put(actions.addShopTournamentRequestSuccess(response.data));
  } catch (error) {
    yield put(actions.addShopTournamentRequestFailure(error.response.data));
  }
}

// 매장 대회 수정
function* modifyShopTournamentRequestSaga() {
  try {
    const tournamentOpen = yield select((state) => state.tournamentOpen);

    const awards = [...tournamentOpen.data.awards];

    let newAwards = awards.map((awards) => {
      if (awards.awardItems === 'HoleInOne' || awards.awardItems === 'CourseRoundPlayCount') {
        // 홀인원, 참가 라운드 횟수는 종합 랭킹이므로 코스ID 지정하지 않도록 예외처리
        return { ...awards, courseId: '' };
      } else {
        if (tournamentOpen.data.courseRounds.length === 1) {
          return { ...awards, courseId: tournamentOpen.data.courseRounds[0].courseId };
        } else {
          return { ...awards, courseId: null };
        }
      }
    });

    const data = {
      tournamentTypeCode: tournamentOpen.data.tournamentTypeCode,
      title: tournamentOpen.data.title,
      info: tournamentOpen.data.info,
      summary: tournamentOpen.data.summary,
      thumbnailKey: tournamentOpen.data.thumbnailKey,
      isParticipationTargetLimit: tournamentOpen.data.isParticipationTargetLimit,
      participationTargets: tournamentOpen.data.participationTargets,
      participationGrades: tournamentOpen.data.participationGrades,
      isParticipationConditionsLimit: tournamentOpen.data.isParticipationConditionsLimit,
      participationConditions: tournamentOpen.data.participationConditions,
      startTime: tournamentOpen.data.startTime,
      endTime: tournamentOpen.data.endTime,
      password: tournamentOpen.data.password,
      isShotLiveBroadcast: tournamentOpen.data.isShotLiveBroadcast,
      courseRounds: tournamentOpen.data.courseRounds,
      gameHoleType: tournamentOpen.data.gameHoleType,
      gameSettingLevel: tournamentOpen.data.gameSettingLevel,
      difficulty: tournamentOpen.data.difficulty,
      greenSpeed: tournamentOpen.data.greenSpeed,
      greenHardness: tournamentOpen.data.greenHardness,
      swingPlate: tournamentOpen.data.swingPlate,
      holecupLocation: tournamentOpen.data.holecupLocation,
      concede: tournamentOpen.data.concede,
      greenLocation: tournamentOpen.data.greenLocation,
      mulliganCount: tournamentOpen.data.mulliganCount,
      puttingLineCount: tournamentOpen.data.puttingLineCount,
      windSpeed: tournamentOpen.data.windSpeed,
      isCalculationGameRoundCount: tournamentOpen.data.isCalculationGameRoundCount,
      isCalculationBackCount: tournamentOpen.data.isCalculationBackCount,
      isCalculationLastRoundStartTime: tournamentOpen.data.isCalculationLastRoundStartTime,
      isAward: tournamentOpen.data.isAward,
      awards: newAwards,
      participationShops: tournamentOpen.data.participationShops,
      isParticipationGradeLimit: tournamentOpen.data.isParticipationGradeLimit,
      isUseGuideDistance: tournamentOpen.data.isUseGuideDistance,
    };

    const response = yield call(callModifyTournament, tournamentOpen.tournamentId, data);

    yield put(actions.modifyShopTournamentRequestSuccess(response.data));
  } catch (error) {
    yield put(actions.addShopTournamentRequestFailure(error.response.data));
  }
}

// Sign-In Sagas
export default function* root() {
  yield all([takeLatest(TournamentOpen.LOAD_SHOP_TOURNAMENT_RAW_DATA_REQUEST, loadShopTournamentRawDataRequestSaga)]);
  yield all([takeLatest(TournamentOpen.LOAD_SHOP_TOURNAMENT_TERMS_REQUEST, loadShopTournamentTermsRequestSaga)]);
  yield all([takeLatest(TournamentOpen.LOAD_SHOP_TOURNAMENT_COURSE_LIST_REQUEST, loadShopTournamentCourseListRequestSaga)]);
  yield all([takeLatest(TournamentOpen.LOAD_SHOP_TOURNAMENT_COURSE_DETAILS_REQUEST, loadShopTournamentCourseDetailsRequestSaga)]);
  yield all([takeLatest(TournamentOpen.ADD_SHOP_TOURNAMENT_REQUEST, addShopTournamentRequestSaga)]);
  yield all([takeLatest(TournamentOpen.MODIFY_SHOP_TOURNAMENT_REQUEST, modifyShopTournamentRequestSaga)]);
}
